/**
 * Provides delivery information for the product available on the page (product information page).
 */
class ProductDeliveryOptions extends HTMLElement {
    static SAME_DAY = 'sameday';
    static EXPRESS = 'express';
    static STANDARD = 'standard';
    static CLICK_COLLECT = 'click_collect';
    static DELIVERY_METHOD_ENDPOINT = `${window.location.origin}/a/shipping/delivery-method/product/status`;

    static POSTCODE_STORAGE = 'delivery_postcode';

    #postcode = null;
    #size = null;

    constructor() {
        super();

        document.querySelector(`#product-delivery-option-opener`).addEventListener('click',
            debounce(this.viewDeliveryOptions)
        );

        document.querySelector(`select[name="delivery-size"]`).addEventListener('change',
            debounce(this.onSizeSelection.bind(this))
        );

        document.querySelector(`input[name="postcode"]`).addEventListener('keyup',
            debounce(this.onPostcodeEntry.bind(this), 500)
        );

        this.onPageLoad().then();
    }

    /**
     * Opens the side panel delivery option information.
     */
    viewDeliveryOptions() {
        document.querySelector('body').classList.add('open-cc');
        document.querySelector('.side-panel.delivery-filter-drawer').classList.add('active');
    }

    /**
     * Auto handles the postcode entry process if a postcode data is available on the local storage.
     *
     * @return {Promise<void>}
     */
    async onPageLoad() {
        const postcodeCandidate = localStorage.getItem(ProductDeliveryOptions.POSTCODE_STORAGE);
        const postcodeInput = document.querySelector(`input[name="postcode"]`);

        if (!postcodeCandidate || !postcodeInput) return;

        postcodeInput.value = postcodeCandidate;

        await this.onPostcodeEntry();
    }

    /**
     * On size selection, if the postcode is available on the input field, performs delivery methods availability
     * for the selected size of the product.
     */
    async onSizeSelection() {
        const sizeSelector = document.querySelector(`select[name="delivery-size"]`);
        // Message box that gets displayed when no size is selected.
        const unselectedSizeNotices = document.querySelectorAll(`.size-unselected`);
        // Message box that gets displayed when a size is selected by the user.
        const sizeSelectedInfo = document.querySelectorAll(`.size-selected`);

        if (!sizeSelector || this.#size === sizeSelector.value) return;

        this.#size = sizeSelector.value;

        // When none of the available sizes is selected, provide the "size select" message.
        if (!sizeSelector.value) {
            unselectedSizeNotices.forEach(item => item.classList.remove('hide'));
            sizeSelectedInfo.forEach(item => item.classList.add('hide'));

            // Hide the availability status message.
            ['sameday-info', 'click-collect-info'].forEach(methodContent => {
                document.querySelector(`#${methodContent} .available`)?.classList?.add('hide');
                document.querySelector(`#${methodContent} .unavailable`)?.classList?.add('hide');
            });
        } else {
            unselectedSizeNotices.forEach(item => item.classList.add('hide'));
        }

        await this.onPostcodeEntry(true);
    }

    /**
     * Handles delivery methods availability check after a valid postcode entry has been made by the user.
     */
    async onPostcodeEntry(sizeChanged = false) {
        const postcodeCandidate = document.querySelector(`input[name="postcode"]`)?.value;
        const selectedSize = document.querySelector(`select[name="delivery-size"]`)?.value;

        // Don't process the request if there's no change in both the entered postcode and the selected size.
        if (!postcodeCandidate || (this.#postcode === postcodeCandidate && !sizeChanged)) return;

        this.#postcode = postcodeCandidate;
        localStorage.setItem(ProductDeliveryOptions.POSTCODE_STORAGE, this.#postcode);

        // Home and pick up HTML containers.
        const homeDeliveryContents = document.querySelector(`#home-delivery`);
        const pickupContents = document.querySelector(`#click-collect`);
        const invalidPostcodeMessage = document.querySelector(`#product-delivery-postcode-error`);

        const isValidPostcode = this.#isValidPostcode(postcodeCandidate);

        // Display the delivery HTML contents only if the given postcode is valid.
        [ homeDeliveryContents, pickupContents ].forEach(deliveryItem => {
            isValidPostcode ? deliveryItem.classList.remove('hide') : deliveryItem.classList.add('hide');
        });

        // Handle invalid postcode error message.
        if (!isValidPostcode) {
            // Display the error message only if there is a character available on the postcode input.
            postcodeCandidate.toString().length ? invalidPostcodeMessage.classList.remove('hide') : invalidPostcodeMessage.classList.add('hide');
            return;
        }

        // Remove the invalid postcode error message.
        invalidPostcodeMessage.classList.add('hide');

        await this.#checkDeliveryAvailability(!!selectedSize);
    }

    #isValidPostcode(postcode) {
        return !isNaN(postcode) && postcode.toString().length === 4;
    }

    /**
     * Determines availability of different shipping methods for the product.
     * Same day and click & collect delivery options' availability are only checked if any of the available size is selected by the user.
     *
     * @param isSizeSelected
     * @return {Promise<void>}
     */
    async #checkDeliveryAvailability(isSizeSelected = false) {
        const loadingOverlay = document.querySelector(`.delivery-filter-drawer .loading-overlay`);

        // Display loading GIF while the request is being processed.
        if (loadingOverlay) loadingOverlay.classList.add('show');

        const methodsToCheck = [
            ...(isSizeSelected ? [ProductDeliveryOptions.SAME_DAY, ProductDeliveryOptions.CLICK_COLLECT] : []),
            ProductDeliveryOptions.EXPRESS,
            ProductDeliveryOptions.STANDARD
        ];

        const availabilityData = await this.#deliveryAvailabilityRequest(methodsToCheck);

        const deliveryMethods = Object.keys(availabilityData);

        if (!deliveryMethods.length) {
            if (loadingOverlay) loadingOverlay.classList.remove('show');

            return;
        }

        deliveryMethods.forEach(method => {
            if (!methodsToCheck.includes(method)) return;

            this.#deliveryAvailabilityMessage(method, availabilityData[method]);
        });

        // Remove the loading GIF as the request is now processed.
        if (loadingOverlay) loadingOverlay.classList.remove('show');
    }

    /**
     * Handles display of availability and estimation message`
     * @param deliveryMethod
     * @param deliveryMethodData
     */
    #deliveryAvailabilityMessage(deliveryMethod, deliveryMethodData) {
        const deliveryContainerId = deliveryMethod.replace('_', '-') + '-info';
        const deliveryEstimationContainer = document.querySelector(`#${deliveryMethod.replace('_', '-')}-estimation`);

        let statusToShow, statusToHide;

        if ('availability' in deliveryMethodData && deliveryMethodData.availability) {
            statusToShow = 'available';
            statusToHide = 'unavailable';

            deliveryEstimationContainer?.classList?.remove('hide');
            if ('estimation' in deliveryMethodData) deliveryEstimationContainer.innerHTML = deliveryMethodData.estimation;
        } else {
            statusToShow = 'unavailable';
            statusToHide = 'available';

            document.querySelector(`#${deliveryMethod.replace('_', '-')}-estimation`)?.classList?.add('hide');
            deliveryEstimationContainer.innerHTML = '';
        }

        document.querySelector(`#${deliveryContainerId} .${statusToShow}`)?.classList?.remove('hide');
        document.querySelector(`#${deliveryContainerId} .${statusToHide}`)?.classList?.add('hide');
    }

    /**
     * Makes a REST API request to determine availability of different shipping methods for the product.
     * @param methodsToCheck List of shipping methods to check for availability.
     * @return {Promise<void>}
     */
    async #deliveryAvailabilityRequest(methodsToCheck) {
        let endpoint = `${ProductDeliveryOptions.DELIVERY_METHOD_ENDPOINT}?postcode=${this.#postcode}&store=us`;

        this.#getSizeToFetch().forEach(size => endpoint += `&skus=${size}`)

        methodsToCheck.forEach(method => endpoint+= `&methods=${method}`);

        const response = await fetch(endpoint);

        return (await response.json())?.data ?? [];
    }

    /**
     * Provides all sizes that must be considered for the delivery option. If none of the available sizes are selected,
     * delivery option result is based on all available sizes of the product.
     *
     * @return {*[]}
     */
    #getSizeToFetch() {
        const selectedSize = document.querySelector(`select[name="delivery-size"]`)?.value;

        if (selectedSize) return [ selectedSize ];

        let allAvailableSizes = [];

        document.querySelectorAll(`.product-size:not(.out-of-stock)`).forEach(size => {
            if (size?.value) allAvailableSizes.push(size.value);
        });

        return allAvailableSizes;
    }
}

customElements.define('product-delivery-options', ProductDeliveryOptions);

window.addEventListener('load', () => {
    new ProductDeliveryOptions();
});